// Party panel + toast overlay (Phase 5 Step 16b).
//
// Two PixiJS renderers:
//   - PartyPanel: modal panel toggled by KeyP. Shows pending invites +
//     roster. Buttons: Accept / Decline on each invite; Leave if in a
//     roster. No kick in Phase 5.
//   - ToastOverlay: top-center notifications pushed by party-state
//     (e.g. partyInvite). Click on a partyInvite toast opens the
//     panel. 3s fade.
//
// Modal + input-suppression: main.ts calls local.setModal(true/false)
// around open/close — matches the Phase 3/4 panel convention
// (workbench-ui.ts, market-ui.ts).

import { Application, Container, Graphics, Text } from "pixi.js";
import type { ClientMessage, PlayerState } from "../../shared/messages.ts";
import type { NearbyPlayer } from "../../shared/friends-messages.ts";
import { resolveDisplayName } from "./display-name-resolver.ts";
import {
  buildAcceptPartyInviteMessage,
  buildDeclinePartyInviteMessage,
  buildEnterExpeditionMessage,
  buildInviteToPartyMessage,
  buildLeavePartyMessage,
} from "../../shared/expedition-messages.ts";
import {
  pruneExpiredInvites,
  type PartyClientState,
} from "./party-state.ts";

// ---- layout constants ----
const PANEL_W = 360; // contracts:allow-literal — party panel width
const PANEL_H = 420; // contracts:allow-literal — party panel height
const PAD = 12; // contracts:allow-literal — inner padding
const ROW_H = 28; // contracts:allow-literal — row height
const BTN_W = 72; // contracts:allow-literal — button width
const BTN_H = 22; // contracts:allow-literal — button height
const PANEL_BG = 0x12122a;
const PANEL_BORDER = 0x4455cc;
const ROW_BG = 0x1a1a3a;
const BTN_BG = 0x2a2a55;
const BTN_BG_HOVER = 0x3a3a66;
const TEXT_COLOR = 0xeeeeff;
const MUTED_COLOR = 0x8888aa;


export type PartyUIDeps = {
  app: Application;
  selfId: () => string | null;
  send: (msg: ClientMessage) => void;
  onOpen: () => void;
  onClose: () => void;
  // Phase 7 Block B — name resolution sources for roster rows.
  selfPlayer?: () => PlayerState | null;
  zonePlayers?: () => Map<string, PlayerState>;
  // Phase 7 Block F — proximity invite.
  onRequestNearbyPlayers?: () => void;
};

export class PartyUI {
  private readonly root = new Container();
  private readonly body = new Container();
  private _isOpen = false;
  private _nearbyPlayers: NearbyPlayer[] = [];
  private _showingNearbyList = false;

  constructor(private readonly deps: PartyUIDeps) {
    this.root.visible = false;
    this.root.zIndex = 120; // contracts:allow-literal — above world + HUD
    deps.app.stage.sortableChildren = true;
    deps.app.stage.addChild(this.root);

    const bg = new Graphics()
      .rect(0, 0, PANEL_W, PANEL_H)
      .fill({ color: PANEL_BG })
      .stroke({ color: PANEL_BORDER, width: 2 }); // contracts:allow-literal — border width
    this.root.addChild(bg);

    const title = new Text({
      text: "Party",
      style: { fontSize: 18, fill: TEXT_COLOR, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — title font
    });
    title.x = PAD;
    title.y = PAD;
    this.root.addChild(title);

    this.body.x = PAD;
    this.body.y = PAD + 28; // contracts:allow-literal — title spacer
    this.root.addChild(this.body);

    this.layout();
    deps.app.renderer.on("resize", () => this.layout());
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  open(state: PartyClientState): void {
    this._isOpen = true;
    this.root.visible = true;
    this.render(state);
    this.deps.onOpen();
  }

  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    this._showingNearbyList = false;
    this.root.visible = false;
    this.deps.onClose();
  }

  // Called each frame while open so the roster / invite list stay
  // current with the reducer. Cheap re-render: clear `body` then draw.
  refresh(state: PartyClientState): void {
    if (!this._isOpen) return;
    this.render(state);
  }

  private render(state: PartyClientState): void {
    this._lastState = state;
    // Tear down existing children; PixiJS `removeChildren` returns the
    // list so we destroy each to release GPU textures.
    for (const child of this.body.removeChildren()) child.destroy();

    const now = Date.now();
    pruneExpiredInvites(state, now);

    const selfId = this.deps.selfId();
    let y = 0;

    // Nearby-players invite sub-view.
    if (this._showingNearbyList) {
      y = this.drawHeader("Invite Nearby Player", y);
      if (this._nearbyPlayers.length === 0) {
        y = this.drawMuted("Loading nearby players…", y);
      } else {
        for (const p of this._nearbyPlayers) {
          const label = p.displayName || p.playerId;
          y = this.drawButton(label, y, () => {
            this.deps.send(buildInviteToPartyMessage(p.playerId));
            this._showingNearbyList = false;
            this.close();
          });
        }
      }
      y = this.drawButton("Back", y, () => {
        this._showingNearbyList = false;
        this.render(state);
      });
      void selfId;
      void y;
      return;
    }

    // Solo empty-state: no roster AND no pending invites → hint + invite button.
    if (state.roster === null && state.pendingInvites.length === 0) {
      y = this.drawMuted("Invite a nearby player to enable expeditions.", y);
      y += PAD;
      y = this.drawButton("Invite", y, () => this.promptInvite(state));
      void selfId;
      void y;
      return;
    }

    // ---- pending invites section ----
    y = this.drawHeader("Pending Invites", y);
    if (state.pendingInvites.length === 0) {
      y = this.drawMuted("(none)", y);
    } else {
      for (const invite of state.pendingInvites) {
        y = this.drawInviteRow(invite, y);
      }
    }

    y += PAD;

    // ---- roster section ----
    y = this.drawHeader("Roster", y);
    if (state.roster === null) {
      y = this.drawMuted("Not in a party.", y);
    } else {
      const nameSrc = {
        self: this.deps.selfPlayer?.() ?? null,
        players: this.deps.zonePlayers?.() ?? new Map<string, PlayerState>(),
      };
      const leaderLabel = `${resolveDisplayName(state.roster.leaderId, nameSrc)} (leader)`;
      y = this.drawRosterRow(state.roster.leaderId, leaderLabel, y);
      for (const member of state.roster.members) {
        if (member.memberId === state.roster.leaderId) continue;
        y = this.drawRosterRow(member.memberId, resolveDisplayName(member.memberId, nameSrc), y);
      }
    }

    y += PAD;

    // ---- actions ----
    if (state.roster !== null && state.roster.leaderId === selfId) {
      y = this.drawButton("Invite", y, () => this.promptInvite(state));
      y = this.drawButton("Enter Expedition", y, () => {
        this.deps.send(buildEnterExpeditionMessage("derelict-ship"));
        this.close();
      });
    }
    if (state.roster !== null) {
      y = this.drawButton("Leave", y, () => {
        this.deps.send(buildLeavePartyMessage());
        this.close();
      });
    }
    void y;
  }

  private drawHeader(label: string, y: number): number {
    const t = new Text({
      text: label,
      style: { fontSize: 13, fill: TEXT_COLOR, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — header font
    });
    t.y = y;
    this.body.addChild(t);
    return y + ROW_H;
  }

  private drawMuted(label: string, y: number): number {
    const t = new Text({
      text: label,
      style: { fontSize: 12, fill: MUTED_COLOR, fontFamily: "monospace" }, // contracts:allow-literal — muted font
    });
    t.y = y;
    this.body.addChild(t);
    return y + ROW_H;
  }

  private drawInviteRow(invite: { partyId: string; leaderName: string }, y: number): number {
    const row = new Container();
    const bg = new Graphics()
      .rect(0, 0, PANEL_W - PAD * 2, ROW_H - 4) // contracts:allow-literal — row inset
      .fill({ color: ROW_BG });
    row.addChild(bg);

    const label = new Text({
      text: `Invite from ${invite.leaderName}`,
      style: { fontSize: 12, fill: TEXT_COLOR, fontFamily: "monospace" }, // contracts:allow-literal — row font
    });
    label.x = 6; // contracts:allow-literal — label left padding
    label.y = 4; // contracts:allow-literal — label top padding
    row.addChild(label);

    const acceptBtn = buildButton("Accept", () => {
      this.deps.send(buildAcceptPartyInviteMessage(invite.partyId));
      // Clear pending locally; on success the server pushes
      // partyMemberJoined which seeds the roster. Auto-close per spec.
      this.close();
    });
    acceptBtn.x = PANEL_W - PAD * 2 - BTN_W * 2 - 8; // contracts:allow-literal — right-align
    acceptBtn.y = 1;
    row.addChild(acceptBtn);

    const declineBtn = buildButton("Decline", () => {
      this.deps.send(buildDeclinePartyInviteMessage(invite.partyId));
      // Server-side decline still fires; locally we drop the pending
      // row so the UI doesn't show a stale invite until TTL.
      // The caller will re-render on next frame after state mutates.
      // We don't have direct state access here; trigger via UI.
      this.onDeclineHook?.(invite.partyId);
    });
    declineBtn.x = PANEL_W - PAD * 2 - BTN_W - 4; // contracts:allow-literal — right-align
    declineBtn.y = 1;
    row.addChild(declineBtn);

    row.y = y;
    this.body.addChild(row);
    return y + ROW_H;
  }

  private drawRosterRow(memberId: string, label: string, y: number): number {
    const t = new Text({
      text: `• ${label}`,
      style: { fontSize: 12, fill: TEXT_COLOR, fontFamily: "monospace" }, // contracts:allow-literal — roster font
    });
    t.y = y;
    this.body.addChild(t);
    void memberId;
    return y + ROW_H;
  }

  private drawButton(label: string, y: number, onClick: () => void): number {
    const btn = buildButton(label, onClick);
    btn.x = 0;
    btn.y = y;
    this.body.addChild(btn);
    return y + BTN_H + 4; // contracts:allow-literal — bottom gap
  }

  // Called by main.ts when a nearbyPlayers server message arrives.
  applyNearbyPlayers(players: NearbyPlayer[]): void {
    this._nearbyPlayers = players;
    if (this._isOpen && this._showingNearbyList && this._lastState !== null) {
      this.render(this._lastState);
    }
  }

  private _lastState: import("./party-state.ts").PartyClientState | null = null;

  private promptInvite(state: import("./party-state.ts").PartyClientState): void {
    this._showingNearbyList = true;
    this._nearbyPlayers = []; // optimistic clear; server sends fresh list
    if (this.deps.onRequestNearbyPlayers) this.deps.onRequestNearbyPlayers();
    this.render(state);
  }

  // Decline-hook: main.ts wires this so the decline click clears the
  // pending-invite locally without needing the panel to import the
  // reducer module.
  onDeclineHook: ((partyId: string) => void) | undefined;

  private layout(): void {
    const screenW = this.deps.app.renderer.width;
    const screenH = this.deps.app.renderer.height;
    this.root.x = (screenW - PANEL_W) / 2; // contracts:allow-literal — centering idiom
    this.root.y = (screenH - PANEL_H) / 2; // contracts:allow-literal — centering idiom
  }
}

// Helper: builds a clickable button container with hover state.
function buildButton(label: string, onClick: () => void): Container {
  const root = new Container();
  root.eventMode = "static";
  root.cursor = "pointer";
  const bg = new Graphics()
    .rect(0, 0, BTN_W, BTN_H)
    .fill({ color: BTN_BG });
  root.addChild(bg);
  const t = new Text({
    text: label,
    style: { fontSize: 12, fill: TEXT_COLOR, fontFamily: "monospace" }, // contracts:allow-literal — button font
  });
  t.x = (BTN_W - t.width) / 2; // contracts:allow-literal — centering idiom
  t.y = (BTN_H - t.height) / 2; // contracts:allow-literal — centering idiom
  root.addChild(t);
  root.on("pointerover", () => { bg.tint = BTN_BG_HOVER; });
  root.on("pointerout", () => { bg.tint = 0xffffff; });
  root.on("pointerdown", onClick);
  return root;
}

// ToastOverlay extracted to party-toast-ui.ts (Phase 7 Block F §10 cap).
export { ToastOverlay, type ToastOverlayDeps } from "./party-toast-ui.ts";
